import { sha } from '@blocksuite/global/utils';
import type { BlockStdScope } from '@blocksuite/std';
import type {
  BlockModel,
  TransformerMiddleware,
  UploadingAssetEntry,
} from '@blocksuite/store';
import { filter, from, map, mergeMap } from 'rxjs';

import { TelemetryProvider } from '../../services';
import { FetchUtils } from '../utils';

const ALLOWED_FLAVOURS = new Set(['affine:attachment', 'affine:image']);

export const uploadMiddleware = (
  std: BlockStdScope,
  concurrent = 5
): TransformerMiddleware => {
  const blockView$ = std.view.viewUpdated.pipe(
    filter(payload => payload.type === 'block'),
    filter(payload => ALLOWED_FLAVOURS.has(payload.view.model.flavour))
  );

  return ({ assetsManager }) => {
    async function upload(
      model: BlockModel,
      state: UploadingAssetEntry & { abortController?: AbortController }
    ) {
      if (!state.abortController) return null;

      const signal = state.abortController.signal;
      if (signal.aborted) return null;

      // Double check
      if (!model.store.hasBlock(model.id)) return null;

      try {
        signal.throwIfAborted();

        const blobId = await Promise.race([
          (async function processUpload() {
            let blob: Blob;

            // 🔥 支持延迟 fetch 模式：先从 URL 获取图片（直接请求 CDN，不使用代理）
            if (state.fetchUrl && !state.blob) {
              console.log(
                `🔄 [Upload Middleware] Fetching image: ${state.fetchUrl}`
              );
              const res = await FetchUtils.fetchImage(
                state.fetchUrl,
                undefined,
                undefined // 不使用 imageProxy，CDN 通常已配置 CORS
              );
              if (!res) {
                throw new Error(`Failed to fetch image: ${state.fetchUrl}`);
              }
              blob = await res.blob();
              console.log(
                `✅ [Upload Middleware] Image fetched: ${state.fetchUrl}`
              );
            } else if (state.blob) {
              blob = state.blob;
            } else {
              throw new Error('No blob or fetchUrl provided');
            }

            const blobId = await sha(await blob.arrayBuffer());

            assetsManager.getAssets().set(blobId, blob);
            const fileName = blob instanceof File ? blob.name : 'unknown';
            const fileType = fileName?.split('.')?.pop() || 'unknown';
            std.getOptional(TelemetryProvider)?.track('canvas_edit_action', {
              doc_id: model.store.doc.id,
              edit_type: 'attachment_upload',
              block_type: 'affine:attachment',
              attachment_upload_type: fileType,
              attachment_upload_method: state.fetchUrl ? 'lazy_load' : 'paste',
            });

            await assetsManager.writeToBlob(blobId);

            return await new Promise<string | null>(resolve => {
              model.store.withoutTransact(() => {
                if (signal.aborted) return resolve(null);

                model.store.updateBlock(model, state.mapInto(blobId));

                resolve(blobId);
              });
            });
          })(),
          // If the signal is not aborted, it will be in the pending state.
          new Promise<null>(resolve => {
            signal.addEventListener('abort', () => resolve(null), {
              once: true,
            });
            if (signal.aborted) {
              resolve(null);
            }
          }),
        ]);

        return blobId;
      } catch (err) {
        console.error('[Upload Middleware] Upload failed:', err);

        return null;
      }
    }

    const blockViewSubscription = blockView$
      .pipe(
        map(payload => {
          if (assetsManager.uploadingAssetsMap.size === 0) return null;

          const model = payload.view.model;
          if (!assetsManager.uploadingAssetsMap.has(model.id)) return null;

          const state = assetsManager.uploadingAssetsMap.get(model.id)!;

          if (payload.method === 'add') {
            state.abortController = new AbortController();
            return { model, state };
          } else {
            state.abortController?.abort();
            assetsManager.uploadingAssetsMap.delete(model.id);
            return null;
          }
        }),
        filter(Boolean),
        mergeMap(
          ({ model, state }) =>
            from(
              upload(model, state).then(() => {
                assetsManager.uploadingAssetsMap.delete(model.id);
              })
            ),
          concurrent
        )
      )
      .subscribe();

    return () => {
      blockViewSubscription.unsubscribe();
    };
  };
};
